import type {
  AccessoriesFile,
  SelectedAccessories,
  Variant,
  KitAccessory,
  Accessory,
  ColorOption,
  VariantOptions,
  StreamTypes,
  ViewTypes,
  CachedState,
} from "types/app";

export const VARIANT_CATEGORY_PRIORITY: VariantOptions<number> = {
  id: 50,
  code: 10,
  battery: 2,
  variant: 1,
  seating: 1,
};

export const filterVariants = (
  variants: Variant[],
  options: VariantOptions = {},
  strict = true,
) => {
  return variants.filter((variant) => {
    return (strict ? Array.prototype.every : Array.prototype.some).call(
      Object.keys(options),
      (key: string) =>
        key in variant && variant[key as keyof Variant] === options[key as keyof Variant],
    );
  });
};

export const sortVariants = (
  variants: Variant[],
  options: VariantOptions = {},
  preferPriority = false,
) => {
  return variants
    .map((variant) => {
      let score = 0,
        numMatched = 0;

      for (const key in options) {
        if (
          key in variant &&
          typeof options[key as keyof Variant] !== "undefined" &&
          variant[key as keyof Variant] === options[key as keyof Variant]
        ) {
          numMatched++;
          if (key in VARIANT_CATEGORY_PRIORITY)
            score += VARIANT_CATEGORY_PRIORITY[key as keyof typeof VARIANT_CATEGORY_PRIORITY] || 0;
        }
      }

      return { variant, score, numMatched, priority: variant.priority };
    })

    .sort((a, b) => {
      if (preferPriority && a.priority !== b.priority) {
        return b.priority - a.priority;
      }

      if (b.score === a.score) {
        return b.numMatched - a.numMatched;
      }

      return b.score - a.score;
    })
    .map(({ variant }) => {
      return variant;
    });
};

export const findVariant = (variants: Variant[], variantId: Variant["id"]) => {
  return variants.find(({ id }) => id === variantId);
};

export const findColor = (colors: ColorOption[], colorId: ColorOption["id"]) => {
  return colors.find(({ id }) => id === colorId);
};

export const priceFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  // @ts-ignore
  trailingZeroDisplay: "stripIfInteger",
});

export const filterAccessories = (
  selectedAccessories: SelectedAccessories,
  allAccessories: AccessoriesFile,
) => {
  return allAccessories
    .map(({ items, category }) => {
      if (!selectedAccessories[category].length) {
        return [];
      }

      return (items as Array<KitAccessory | Accessory>).filter(({ id }) =>
        selectedAccessories[category].includes(id),
      );
    })
    .flat();
};

export const countAccessories = (selectedAccessories: SelectedAccessories) => {
  return Object.values(selectedAccessories).reduce(
    (prevCount, currCategoryList) => prevCount + currCategoryList.length,
    0,
  );
};

export const normalizeModelCode = (code: string) => {
  return code.replace(/-/g, " ");
};

type GtagVariantData = {
  variant_id: Variant["id"];
  variant_code: Variant["code"];
  selected_color: ColorOption["label"];
  selected_environment: string;
  variant_pack_type: Variant["variant"];
  variant_seat_type: Variant["seating"];
  variant_wheel_type: Variant["wheels"];
  variant_battery_type: Variant["battery"];
  stream_type: StreamTypes;
  view_type: ViewTypes;
} & Record<string, string>;

export const getGtagVariantData = (
  cachedState: CachedState,
  variant: Variant,
  selectedColor: ColorOption,
  streamViewType: StreamTypes,
): GtagVariantData => {
  return {
    variant_id: variant.id,
    variant_code: variant.code,
    selected_color: selectedColor.label,
    selected_environment: cachedState.animations.dayMode ? "day" : "night",
    variant_pack_type: variant.variant,
    variant_seat_type: variant.seating,
    variant_wheel_type: variant.wheels,
    variant_battery_type: variant.battery,
    stream_type: streamViewType,
    view_type: cachedState.view,
  };
};
